import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { EditorCard } from "@/components/EditorCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const TIERS = [
    { v: "econ", l: "Economico (< €8)" },
    { v: "medio", l: "Medio (€8–15)" },
    { v: "premium", l: "Premium (> €15)" },
];

export default function Editors() {
    const [params, setParams] = useSearchParams();
    const [editors, setEditors] = useState([]);
    const [genres, setGenres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    const [q, setQ] = useState(params.get("q") || "");
    const [tier, setTier] = useState(params.get("price_tier") || "");
    const [location, setLocation] = useState(params.get("location") || "");
    const [genre, setGenre] = useState(params.get("genre") || "");
    const [minPositive, setMinPositive] = useState(0);
    const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified") === "true");
    const [sort, setSort] = useState("reviews");

    useEffect(() => { api.get("/editors/genres/all").then(({ data }) => setGenres(data)); }, []);

    const fetchEditors = useCallback(async () => {
        setLoading(true);
        const p = {};
        if (q) p.q = q;
        if (tier) p.price_tier = tier;
        if (location) p.location = location;
        if (genre) p.genre = genre;
        if (minPositive) p.min_positive = minPositive;
        if (verifiedOnly) p.verified_only = true;
        p.sort = sort;
        const { data } = await api.get("/editors", { params: p });
        setEditors(data);
        setLoading(false);
    }, [q, tier, location, genre, minPositive, verifiedOnly, sort]);

    useEffect(() => { fetchEditors(); }, [fetchEditors]);

    const reset = () => { setQ(""); setTier(""); setLocation(""); setGenre(""); setMinPositive(0); setVerifiedOnly(false); setSort("reviews"); setParams({}); };

    const Filters = (
        <div className="space-y-7">
            <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Ricerca</Label>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome o parola chiave" className="mt-2 bg-card" data-testid="filter-search" />
            </div>
            <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fascia di prezzo</Label>
                <div className="mt-2 space-y-1.5">
                    {TIERS.map((t) => (
                        <button key={t.v} onClick={() => setTier(tier === t.v ? "" : t.v)} data-testid={`filter-tier-${t.v}`}
                            className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors ${tier === t.v ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}>
                            {t.l}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Località</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Città o regione" className="mt-2 bg-card" data-testid="filter-location" />
            </div>
            <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Genere letterario</Label>
                <Select value={genre || "all"} onValueChange={(v) => setGenre(v === "all" ? "" : v)}>
                    <SelectTrigger className="mt-2 bg-card" data-testid="filter-genre"><SelectValue placeholder="Tutti i generi" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutti i generi</SelectItem>
                        {genres.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Min. recensioni positive: <span className="text-primary font-mono-metric">{minPositive}</span></Label>
                <Slider value={[minPositive]} onValueChange={([v]) => setMinPositive(v)} max={10} step={1} className="mt-4" data-testid="filter-min-positive" />
            </div>
            <div className="flex items-center justify-between pt-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Solo verificati</Label>
                <Switch checked={verifiedOnly} onCheckedChange={setVerifiedOnly} data-testid="filter-verified-only" />
            </div>
            <Button variant="ghost" onClick={reset} className="w-full gap-2 text-muted-foreground" data-testid="filter-reset"><X size={15} /> Azzera filtri</Button>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-serif font-medium">Editor freelance</h1>
                <p className="text-muted-foreground mt-2">Trova il professionista giusto per il tuo manoscritto.</p>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-10">
                <aside className="hidden lg:block">
                    <div className="sticky top-24 bg-card border border-border rounded-lg p-6">{Filters}</div>
                </aside>

                <div>
                    <div className="flex items-center justify-between mb-6 gap-3">
                        <Button variant="outline" className="lg:hidden gap-2" onClick={() => setShowFilters(true)} data-testid="mobile-filters-btn">
                            <SlidersHorizontal size={16} /> Filtri
                        </Button>
                        <p className="text-sm text-muted-foreground hidden sm:block">{editors.length} editor trovati</p>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger className="w-[200px] bg-card" data-testid="sort-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="reviews">Più recensiti</SelectItem>
                                <SelectItem value="trust">Prima i verificati</SelectItem>
                                <SelectItem value="rating">Miglior valutazione</SelectItem>
                                <SelectItem value="price_asc">Prezzo più basso</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="grid sm:grid-cols-2 gap-6">
                            {[0, 1, 2, 3].map((i) => <div key={i} className="h-80 rounded-lg bg-secondary animate-pulse" />)}
                        </div>
                    ) : editors.length === 0 ? (
                        <div className="text-center py-24 text-muted-foreground" data-testid="no-results">
                            Nessun editor corrisponde ai filtri selezionati.
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
                            {editors.map((e, i) => <EditorCard key={e.editor_id} editor={e} index={i} />)}
                        </div>
                    )}
                </div>
            </div>

            {showFilters && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
                    <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-background p-6 overflow-y-auto animate-fade-in">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-serif text-2xl">Filtri</h3>
                            <button onClick={() => setShowFilters(false)}><X /></button>
                        </div>
                        {Filters}
                        <Button className="w-full mt-6 bg-primary" onClick={() => setShowFilters(false)}>Mostra risultati</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
